<template>
  <smart-dialog :title="dialogTitle" :value="value" :width="800" @input="onDialogInput">
    <v-container v-if="context !== 'DELETE'" class="pt-8 px-8">
      <v-form  ref="prestaIssueForm" v-model="valid" @submit.prevent="onSubmit()">
        <v-row align="stretch">
          <v-col cols="8">
            <v-text-field
                v-model="editedIssue.label"
                label="Libellé"
                :rules="[...rules.nonEmptyTrim(), ...rules.maxLength(255, 'Limité à 255 caractères.')]"
            />
          </v-col>
          <v-col cols="4" class="">
            <v-select :items="tagsAsItem"
                      label="Type du litige"
                      class=""
                      v-model="editedIssue.selectedTagId" />
          </v-col>
          <v-col cols="12" class="">
            <v-textarea
                v-model="editedIssue.content.comment"
                class=""
                auto-grow
                label="Description du litige"
                :rules="rules.nonEmptyTrim()"
            />
          </v-col>
          <v-col cols="12">
              <v-divider class="mb-8"/>
              <v-select
                  :disabled="!canSwapPresta"
                  :items="prestaItems"
                  label="Prestation concernée"
                  v-model="editedIssue.selectedPrestaId" />
              <p class="font-weight-bold grey--text">Fournisseur</p>
              <p>{{currentSupplierAccountingName}}</p>
          </v-col>

          <v-col cols="12">
            <p class="red--text" style="min-height: 24px;">{{error}}</p>
          </v-col>

        </v-row>
      </v-form>
    </v-container>

    <v-container v-else>
      <v-row class="justify-center">
        <v-col cols="2" class="d-flex align-center justify-center">
          <v-icon color="blue darken-2" size="80">mdi-information-slab-box</v-icon>
        </v-col>

        <v-col cols="10">
          <p class="font-weight-medium mb-1 grey--text">Confirmer la suppression du litige</p>
          <p>Cette action doit être utilisée uniquement pour supprimer un litige créé par erreur.</p>
          <p>
            Pour conserver l’historique des échanges, préférez les actions
            <span class="chip caption">clore</span> ou <span class="chip caption">classer sans suite</span>.
          </p>
        </v-col>
        <template v-if="error">
          <v-col cols="2"/>
          <v-col cols="10">
            <p class="red--text" style="min-height: 24px;">{{error}}</p>
          </v-col>
        </template>
      </v-row>
    </v-container>


    <template #actions>
      <smart-btn secondary @click.native="closeDialog">
        Annuler
      </smart-btn>

      <smart-btn  v-if="context === 'EDIT' || context === 'CREATE'"
                  :disabled="disableSubmit" primary @click.native="onSubmit"
      >
        {{ context === 'EDIT' ? 'Enregistrer' : 'Créer le litige' }}
      </smart-btn>

      <smart-btn v-else :disabled="loading" primary action="delete" @click.native="onDelete">
        Supprimer
      </smart-btn>
    </template>
  </smart-dialog>
</template>


<script>
import FormMixin from "@/components/mixins/FormMixin";
import {post} from "@/utils/api";

export default {
  name: "BookingPrestaIssueEditDialog",
  mixins: [FormMixin],
  components: {  },
  emits: ['input', 'done'],
  props: {
    value: { type: Boolean, default: false },   // [N] ouverture du dialog
    prestaItems: { type: Array, required: true },
    tags: { type: Array, required: true },
    issue: { type: [Object, null], default: null },
    context: { type: String, required: true },   // CREATE | EDIT | DELETE
    canSwapPresta: { type: Boolean, required: true},
    initialIndex: { type: Number, required: true},
  },
  data() {
    return {
      valid: true,
      loading: false,
      error: null,
      editedIssue: {
        tags: [{"id":1, "tag":"Type de litige 1"}],
        label: null,
        selectedPrestaId: null,
        selectedTagId: null,
        content: {
          userId: null,
          username: null,
          comment: null,
          createdAt: null,
        }
      }
    }
  },
  created() {
    this.init()
  },
  computed: {
    dialogTitle() {
      if (this.context === "CREATE") return "Nouveau litige"
      if (this.context === "DELETE") return "Supprimer le litige"
      return "Modifier le litige"
    },
    tagsAsItem() {
      return this.editedIssue.tags.map((t) => {return {value:t.id, text:t.tag}})
    },
    currentSupplierAccountingName() {
      const presta = this.prestaItems.find(
          i => i.value === this.editedIssue.selectedPrestaId
      )

      return presta?.supplierAccountingName ?? ""
    },
    disableSubmit() {
      return (this.error !== null || this.loading || !this.valid)
    },
    firstComment() {
      return this.issue.comments.length > 0 ? this.issue.comments[0].comment : null
    },
  },
  methods: {
    init() {
      if (this.context === "CREATE") {
        this.editedIssue.tags = this.tags
        if (this.tagsAsItem.length > 0) {
          this.editedIssue.selectedTagId = this.tagsAsItem[0].value
        }
        this.editedIssue.selectedPrestaId = this.prestaItems[this.initialIndex].value
      } else if (this.context === "EDIT") {
        this.editedIssue = {
          ...this.issue,
          tags: this.tags,
          selectedTagId: this.issue.tagId,
          selectedPrestaId: this.issue.prestationId,
          content: {
            userId: null,
            username: null,
            comment: this.firstComment,
            createdAt: null,
          }
        }
      } else if (this.context === "DELETE") {
        this.editedIssue = this.issue
      }
    },
    onDialogInput(value) {
      if (!value) {
        this.closeDialog()
        return
      }
      this.$emit("input", true)
    },
    closeDialog() {
      this.error = null
      this.loading = false
      this.$emit("input", false)
    },
    onSubmit() {
      if (!this.$refs.prestaIssueForm.validate()) return
      this.loading = true

      if (this.context === 'CREATE') this.createIssue()
      else if (this.context === 'EDIT') this.updateIssue()
      else {
        this.loading = false
        this.error = 'Une erreure est survenue, veuillez réessayer ultérieurement.'
      }
    },
    createIssue() {
      if (!this.editedIssue.selectedTagId || !this.editedIssue.selectedPrestaId) {
        this.error = 'Impossible de créer un nouveau litige actuellement.'
        return
      }

      const url = `/api/prestation/${this.editedIssue.selectedPrestaId}/prestation-issue`
      const payload = {
        tagId: this.editedIssue.selectedTagId,
        label: this.editedIssue.label,
        comments: [this.buildFirstComment()]
      }

      post(url, payload)
          .json((newIssue) => {
            this.$emit('done', newIssue)
            this.loading = false
          })
          .catch ((e) => {
            console.log("[PrestaIssueEditDialog][onSubmit] error :", e)
            this.error = 'La création du nouveau litige a rencontré un problème.'
          })
    },
    updateIssue() {
      const url = `/api/prestation/${this.editedIssue.prestationId}/prestation-issue/${this.editedIssue.id}`
      const payload = {
        tagId: this.editedIssue.selectedTagId,
        label: this.editedIssue.label,
        comments: this.rebuildComments(),
        status: this.editedIssue.status,
      }

      post(url, payload)
          .json((issue) => {
            this.$emit('done', issue)
            this.loading = false
          })
          .catch ((e) => {
            console.log("[PrestaIssue][updateIssue] error :", e)
            this.error = 'La mise à jour du litige a rencontré un problème.'
          })
    },
    onDelete() {
      if (!this.editedIssue) {
        this.error = 'Impossible de supprimer le litige actuellement.'
        return
      }
      const issueId = this.editedIssue.id
      const url = `/api/prestation/${this.editedIssue.prestationId}/prestation-issue/${issueId}/delete`

      post(url)
          .res(() => {
            this.$emit('done', null)
          })
          .catch ((e) => {
            console.log("[PrestaIssueEditDialog][deleteIssue] error :", e)
            this.error = 'La suppression du litige a rencontré un problème.'
          })
    },
    buildFirstComment() {
      return {
        userId: this.currentUser.id,
        username: this.username,
        comment: this.editedIssue.content.comment,
      }
    },
    rebuildComments() {
      return [this.buildFirstComment(), ...this.editedIssue.comments.slice(1)]
    },
  } // [N] methods end
}
</script>


<style scoped>
.chip {
  padding: 2px 4px;
  color: #757575;
  background-color: #E0E0E0;
  border-radius: 8px;
}
</style>